import api from '../../services/api';
import type { BandwidthDashboardData, BandwidthPeriodOverTimeTrend } from './bandwidthDashboardTypes';
import type { BandwidthCrossFilterSlice } from './bandwidthCrossFilter';
import type { UsersChartDrill } from './bandwidthUsersDrill';
import { normalizeAssignedToUserIds } from './bandwidthAssigneeFilter';
import { getDurationPresetRange, type DurationPreset } from '../dashboard/utils/dateHelpers';

/** Filter UI sentinel: "All Task Types" selected (omit type filter in API). Not a Prisma TimeEntryType. */
export const BW_ALL_TASK_TYPES_FILTER_VALUE = '__BW_ALL_TASK_TYPES__';

/** Resolves duration filter to API date range (same logic as dashboard query params). */
export function getBandwidthExportDateRange(filterValues: Record<string, string | string[]>): {
  dateFrom: string;
  dateTo: string;
} {
  const timeRange = (filterValues.duration as string) || '';
  let dateFrom = '';
  let dateTo = '';

  if (timeRange.startsWith('custom:')) {
    const [, dates] = timeRange.split('custom:');
    const [from, to] = (dates || '').split(',');
    dateFrom = from || '';
    dateTo = to || '';
  } else if (timeRange.startsWith('preset:')) {
    const presetName = timeRange.replace('preset:', '') as DurationPreset;
    const range = getDurationPresetRange(presetName);
    dateFrom = range.from;
    dateTo = range.to;
  }

  return { dateFrom, dateTo };
}

/**
 * Date range shown on exports and used for team utilization capacity: explicit duration filter when set,
 * otherwise the dashboard API’s resolved bounds (earliest worked date through range end).
 */
export function resolveBandwidthEffectiveExportDateRange(
  filterValues: Record<string, string | string[]>,
  dashboard?: { dashboardRangeFrom?: string; dashboardRangeTo?: string } | null
): { dateFrom: string; dateTo: string } {
  const sel = getBandwidthExportDateRange(filterValues);
  if (sel.dateFrom && sel.dateTo) return sel;
  const df = dashboard?.dashboardRangeFrom?.trim() ?? '';
  const dt = dashboard?.dashboardRangeTo?.trim() ?? '';
  if (df && dt) return { dateFrom: df, dateTo: dt };
  return sel;
}

export function buildBandwidthDashboardQueryParams(
  filterValues: Record<string, string | string[]>,
  opts?: {
    crossFilter?: BandwidthCrossFilterSlice | null;
    applyCountTimePageFilters?: boolean;
    /** When true, send assignmentGroupId from filter values (Tasks & Customers tab drill-down). */
    applyAssignmentGroupFromFilters?: boolean;
    /** When true, send assignedToMe / assignedToUserId page filters. */
    applyAssigneePageFilters?: boolean;
    /** Weekwise tab: legacy alias for assignee filters. Prefer applyAssigneePageFilters. */
    applyUsersPageFilters?: boolean;
    /** When false, omit activity type filter (all types). Default true. */
    applyTimeEntryTypeFilter?: boolean;
    datesDrill?: { month: string | null; weekdayMonFirst: number | null };
    /** Weekwise tab bar click drill (week / type / customer). */
    usersChartDrill?: UsersChartDrill | null;
  }
): Record<string, string> {
  const params: Record<string, string> = {};
  const { dateFrom, dateTo } = getBandwidthExportDateRange(filterValues);

  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const customer = filterValues.customer as string;
  if (customer && customer !== 'All') {
    params.customerId = customer;
  }
  const applyTypes = opts?.applyTimeEntryTypeFilter !== false;
  if (applyTypes) {
    const raw = filterValues.timeEntryType;
    const types = Array.isArray(raw)
      ? raw.filter((t) => t && t !== BW_ALL_TASK_TYPES_FILTER_VALUE)
      : [];
    if (types.length > 0) {
      params.timeEntryType = types.join(',');
    }
  }

  const hourStatuses = filterValues.timeEntryStatus;
  if (Array.isArray(hourStatuses) && hourStatuses.length > 0) {
    params.timeEntryStatus = hourStatuses.join(',');
  }

  const cf = opts?.crossFilter;
  if (cf?.kind === 'user') params.crossFilterUserId = cf.id;
  if (cf?.kind === 'group') params.crossFilterGroupId = cf.id;
  if (cf?.kind === 'customer') params.crossFilterCustomerId = cf.id;

  if (opts?.applyCountTimePageFilters || opts?.applyAssignmentGroupFromFilters) {
    const ag = filterValues.assignmentGroup as string | undefined;
    if (ag && ag !== 'All') {
      params.assignmentGroupId = ag;
    }
  }
  if (
    opts?.applyAssigneePageFilters ||
    opts?.applyCountTimePageFilters ||
    opts?.applyUsersPageFilters
  ) {
    const me = filterValues.assignedToMe as string | undefined;
    if (me === 'true') {
      params.assignedToMe = 'true';
    } else {
      const ids = normalizeAssignedToUserIds(filterValues.assignedToUserId);
      if (ids.length > 0) {
        params.assignedToUserId = ids.join(',');
      }
    }
  }

  const dd = opts?.datesDrill;
  if (dd?.month && /^\d{4}-\d{2}$/.test(dd.month)) {
    params.datesDrillMonth = dd.month;
  }
  if (dd?.weekdayMonFirst != null && dd.weekdayMonFirst >= 0 && dd.weekdayMonFirst <= 6) {
    params.datesDrillWeekdayMonFirst = String(dd.weekdayMonFirst);
  }

  const ud = opts?.usersChartDrill;
  if (ud?.kind === 'week' && /^\d{4}-\d{2}-\d{2}$/.test(ud.weekStart)) {
    params.usersDrillWeekStart = ud.weekStart;
  }
  if (ud?.kind === 'activityType' && ud.activityType.trim()) {
    params.usersDrillActivityType = ud.activityType.trim();
  }
  if (ud?.kind === 'customer' && ud.customerId.trim()) {
    params.crossFilterCustomerId = ud.customerId.trim();
  }

  return params;
}

/** Query params for GET /bandwidth/assignable-users (scope only; no assignee / cross-filter / drill). */
export function buildBandwidthAssignableUsersQueryParams(
  filterValues: Record<string, string | string[]>,
  opts: { includeAssignmentGroup: boolean }
): Record<string, string> {
  return buildBandwidthDashboardQueryParams(filterValues, {
    applyCountTimePageFilters: opts.includeAssignmentGroup,
    applyUsersPageFilters: false,
    applyTimeEntryTypeFilter: true,
  });
}

export async function fetchBandwidthAssignableUsers(
  queryParams: Record<string, string>
): Promise<Array<{ id: string; firstName?: string; lastName?: string; email?: string }>> {
  const response = await api.get<{
    success: boolean;
    data?: { users: Array<{ id: string; firstName?: string; lastName?: string; email?: string }> };
  }>('/bandwidth/assignable-users', { params: queryParams });
  if (!response.data?.success || !response.data.data?.users) {
    throw new Error('Invalid bandwidth assignable users response');
  }
  return response.data.data.users;
}

export async function fetchBandwidthDashboard(
  queryParams: Record<string, string>
): Promise<BandwidthDashboardData> {
  const response = await api.get<{ success: boolean; data: BandwidthDashboardData }>('/bandwidth/dashboard', {
    params: queryParams,
  });
  if (!response.data?.success || !response.data.data) {
    throw new Error('Invalid bandwidth dashboard response');
  }
  const d = response.data.data;
  const periodOverTimeTrend =
    d.periodOverTimeTrend ??
    ({
      granularity: 'month',
      points: (d.monthlyTrend ?? []).map((m) => ({
        periodStart: m.monthStart,
        periodLabel: m.monthLabel,
        totalHours: m.totalHours,
        activityCount: m.activityCount,
      })),
    } satisfies BandwidthPeriodOverTimeTrend);
  return {
    ...d,
    periodOverTimeTrend,
    activityByWeek: d.activityByWeek ?? [],
    userCountVsTime: d.userCountVsTime ?? [],
    dashboardRangeFrom: d.dashboardRangeFrom ?? '',
    dashboardRangeTo: d.dashboardRangeTo ?? '',
  };
}
